import {
  comparatorsByType,
  IDENTITY_OPERATORS_BY_TYPE,
  isListComparator,
  isPreset,
  isValueless,
  LIST_COMPARATOR_BASES,
  listComparators,
  presenceComparators,
  presetsByType,
  upgradeLegacyFilter,
  valueComparatorsByType,
  type Filter,
  type FilterComparator,
  type FilterDataType,
} from "@/shared/domain/query/filters";

import type { FilterDefinition } from "./FilterDefinition";

// The filter WIRE model — row variants, comparator/preset vocabulary, the
// `filterSchema` validator — lives in the query domain, twinned with the
// backend's `roboto.query.filters`. Re-exported here so this layer's many
// importers keep one path. What remains below is UI policy: which operators a
// given field OFFERS in the picker, decided off `FilterDefinition` (a UI
// config type), while the domain model says what the wire ACCEPTS.
export * from "@/shared/domain/query/filters";

// Membership fields are the only ones with no nullability axis: an empty tag list is
// not the same as an absent one, so "is set" has nothing to ask. Every other type
// offers the presence pair when its field is `nullable`.
//
// Stated as the exception rather than as a list of the types that qualify, because the
// list form silently omitted `metric` — the operator picker never offered a pair that
// `comparatorsByType` accepted and the session query path implements
// (`_METRIC_COMPARATOR_TO_SUBQUERY_OPERATORS` maps IS_NULL to a NOT EXISTS over the
// metrics table). A new field type now gets the presence axis by default, and opting
// out is a deliberate edit here.
const TYPES_WITHOUT_PRESENCE_AXIS: ReadonlySet<FilterDataType> = new Set([
  "set",
]);

/**
 * What the picker offers the checklist types. The wire accepts more for both
 * (`comparatorsByType`, in step with the backend, which still reads `EQUALS`
 * and the presence checks from stored rows and older clients), but each field
 * gets one editor per question:
 *
 * - An enum is a checklist, "is" (`ANY_OF`) or "is not" (`NONE_OF`). One
 *   ticked box already says "is", and "Not set" is a row of the checklist on a
 *   nullable field rather than an operator of its own.
 * - A nullable boolean is the True / False / Not set checklist (`ANY_OF`),
 *   which says everything `EQUALS` and the presence checks did.
 * - A boolean that is never unset is the True / False pair (`EQUALS`). A
 *   checklist there could only add "either", which filters nothing.
 */
const MULTISELECT_ENUM_COMPARATORS = [
  "ANY_OF",
  "NONE_OF",
] as const satisfies readonly FilterComparator[];

const MULTISELECT_NULLABLE_BOOLEAN_COMPARATORS = [
  "ANY_OF",
] as const satisfies readonly FilterComparator[];

const MULTISELECT_BOOLEAN_COMPARATORS = [
  "EQUALS",
] as const satisfies readonly FilterComparator[];

/**
 * A string field's comparators with its lists first, each in place of the
 * equality it generalizes: "is any of" one value is "equals" it, and "is none
 * of" one value is "not equals" it, so the picker offers one form, the list,
 * and "is any of" leads as the default. Equality stays where its list is not
 * allowed (a trigger's `name`, allowed only `CONTAINS` and `EQUALS`), since it
 * is then the field's only exact match.
 */
function listsInPlaceOfEquality(
  comparators: readonly FilterComparator[],
): readonly FilterComparator[] {
  const lists = listComparators.filter((list) => comparators.includes(list));
  const replaced = new Set<FilterComparator>(
    lists.map((list) => LIST_COMPARATOR_BASES[list]),
  );
  return [
    ...lists,
    ...comparators.filter(
      (comparator) =>
        !isListComparator(comparator) && !replaced.has(comparator),
    ),
  ];
}

/**
 * The value-bearing comparators a field exposes in the OperatorPicker: the
 * type's value set, plus the presence pair when the field is `nullable`.
 * `string` narrows its value set via the optional `allowedComparators`, and
 * offers its lists first, in place of equality (see
 * {@link listsInPlaceOfEquality}). `set` has no presence axis, so `nullable` is
 * ignored for it. `identity` derives its set from the principal types the
 * field declares — one `IS_<TYPE>` each, in declared order. `enum` and
 * `boolean` offer their checklist forms instead (see
 * {@link MULTISELECT_ENUM_COMPARATORS}).
 *
 * The single seam for per-field comparator selection: `OperatorPicker` renders
 * this list and `defaultComparatorFor` reads its head, so the "default is
 * always a visible comparator" invariant holds by construction. Presets are the
 * parallel seam — see {@link effectivePresets}.
 */
export function effectiveComparators(
  definition: FilterDefinition,
): readonly FilterComparator[] {
  if (definition.type === "identity") {
    return definition.options.map(
      (option) => IDENTITY_OPERATORS_BY_TYPE[option.type].comparator,
    );
  }
  if (definition.type === "enum") {
    return MULTISELECT_ENUM_COMPARATORS;
  }
  if (definition.type === "boolean") {
    return definition.nullable
      ? MULTISELECT_NULLABLE_BOOLEAN_COMPARATORS
      : MULTISELECT_BOOLEAN_COMPARATORS;
  }

  const base =
    definition.type === "string"
      ? listsInPlaceOfEquality(
          definition.allowedComparators ?? valueComparatorsByType.string,
        )
      : valueComparatorsByType[definition.type];

  return definition.nullable &&
    !TYPES_WITHOUT_PRESENCE_AXIS.has(definition.type)
    ? [...base, ...presenceComparators]
    : base;
}

/**
 * Whether a query may test `definition`'s field for being unset, that is,
 * whether `IS_NULL` is among the comparators the definition allows: its
 * `allowedComparators` (or, without them, its type's whole vocabulary), plus
 * the presence pair on a `nullable` field, as {@link effectiveComparators}
 * layers it. False where a field narrows its comparators to a set without the
 * null checks, as a resource-id field does: the backend rejects them there.
 *
 * An "is none of" list sends an `IS_NULL` branch to keep the rows with no
 * value (`listToCondition`), which on a field that is never unset simply
 * matches nothing; this is what leaves it out where it would be rejected.
 */
export function acceptsNullChecks(definition: FilterDefinition): boolean {
  const allowed: readonly FilterComparator[] =
    definition.type === "string" && definition.allowedComparators
      ? definition.allowedComparators
      : comparatorsByType[definition.type];
  return (
    allowed.includes("IS_NULL") ||
    (definition.nullable === true &&
      !TYPES_WITHOUT_PRESENCE_AXIS.has(definition.type))
  );
}

/**
 * The comparators the picker lists for a filter under `comparator`: the
 * field's offered ones ({@link effectiveComparators}), plus `comparator`
 * itself when the field does not offer it. That is a stored filter
 * {@link normalizeForDefinition} had no offered form for, such as a
 * never-unset boolean's `ANY_OF [false, true]`; listing its own comparator
 * keeps the picker saying what the filter is, and the author can still move
 * off it. A preset is never added, since presets have a row of their own.
 */
export function pickerComparators(
  definition: FilterDefinition,
  comparator: FilterComparator,
): readonly FilterComparator[] {
  const offered = effectiveComparators(definition);
  return offered.includes(comparator) || isPreset(comparator)
    ? offered
    : [...offered, comparator];
}

/**
 * `filter` in the form `definition`'s picker offers, where one means exactly
 * the same thing, so a stored filter opens, and reads on its chip, the way a
 * fresh one would be built:
 *
 * - An enum takes its list form ({@link upgradeLegacyFilter}). `filterSchema`
 *   already applies that to every stored filter; it is repeated here for rows
 *   assembled without a parse, such as a trigger's restored condition.
 * - On a nullable boolean, `EQUALS x` is `ANY_OF [x]`, `IS_NULL` is the list
 *   of "Not set" alone, and `IS_NOT_NULL` is `ANY_OF [false, true]`.
 * - On a boolean that is never unset, an `ANY_OF` over one value, without
 *   "Not set", is `EQUALS` that value.
 * - On a string field that offers "is any of" in place of equality (see
 *   {@link listsInPlaceOfEquality}), `EQUALS x` is `ANY_OF [x]`, which is sent
 *   and saved as that same `EQUALS` (`listToCondition`, `savedFilters.ts`).
 *   A `NOT_EQUALS` is left as it is: "is none of" says it only with "Not set"
 *   as a member, which is sent as a null check of its own, so the condition
 *   would no longer read back as the one stored.
 *
 * Anything else comes back as it is, and still edits: see
 * {@link pickerComparators}. Idempotent.
 *
 * Kept apart from `filterSchema`'s upgrader because the boolean and string
 * forms depend on the field's `nullable` and comparators, which only the
 * definition knows.
 */
export function normalizeForDefinition(
  filter: Filter,
  definition: FilterDefinition,
): Filter {
  if (filter.type !== definition.type) {
    return filter;
  }
  if (filter.type === "enum") {
    return upgradeLegacyFilter(filter) as Filter;
  }
  if (filter.type === "string") {
    return filter.comparator === "EQUALS" &&
      effectiveComparators(definition).includes("ANY_OF")
      ? { ...filter, comparator: "ANY_OF" }
      : filter;
  }
  if (filter.type !== "boolean") {
    return filter;
  }
  // `includeNull` is dropped from whatever is rebuilt: only a list can hold
  // it, and each case below sets it where the new list needs it.
  const { includeNull: _dropped, ...rest } = filter;
  if (definition.nullable) {
    switch (filter.comparator) {
      case "EQUALS":
        return filter.values.length === 1
          ? { ...rest, comparator: "ANY_OF" }
          : filter;
      case "IS_NULL":
        return { ...rest, comparator: "ANY_OF", values: [], includeNull: true };
      case "IS_NOT_NULL":
        return { ...rest, comparator: "ANY_OF", values: [false, true] };
      default:
        return filter;
    }
  }
  return filter.comparator === "ANY_OF" &&
    filter.values.length === 1 &&
    filter.includeNull !== true
    ? { ...rest, comparator: "EQUALS" }
    : filter;
}

/**
 * The date presets the picker offers, shortest window first.
 *
 * Narrower than the wire vocabulary in `datePresets`, which is the set the backend accepts
 * and must stay in step with it. `LAST_90_DAYS` and `THIS_MONTH` are deliberately absent:
 * a saved filter holding either still parses and still renders its chip, but neither is
 * offered for new filters. Offering policy belongs here rather than in the schema module,
 * which states what is valid rather than what is useful.
 *
 * The hour windows lead because they are measured back from now, so they read the same in
 * every timezone. `TODAY` is anchored to UTC midnight, which makes it a sliver of work for
 * a reader in the Americas during their afternoon.
 */
const OFFERED_DATE_PRESETS = [
  "LAST_3_HOURS",
  "LAST_8_HOURS",
  "LAST_24_HOURS",
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
] as const satisfies readonly FilterComparator[];

/**
 * The presets a field exposes, mirroring {@link effectiveComparators} for the
 * valueless half of the picker. Dates offer {@link OFFERED_DATE_PRESETS} whatever the
 * field, unless it sets `withoutPresets`, which withholds them from a filter that is
 * stored and evaluated later; `identity` narrows to the principal types the field
 * declares, since an `IS_ANY_<TYPE>` for a type the column never holds would match
 * nothing.
 *
 * An identity field's presets are withheld entirely unless it admits more than
 * one principal type. On a single-type field "any user" is `created_by LIKE
 * 'user:%'` — a predicate every row satisfies, so it would sit beside a
 * specific-identity picker looking like a filter while doing nothing.
 */
export function effectivePresets(
  definition: FilterDefinition,
): readonly FilterComparator[] {
  if (definition.type === "date") {
    return definition.withoutPresets ? [] : OFFERED_DATE_PRESETS;
  }
  if (definition.type !== "identity") {
    return presetsByType[definition.type];
  }
  if (definition.options.length < 2) {
    return [];
  }
  return definition.options.map(
    (option) => IDENTITY_OPERATORS_BY_TYPE[option.type].preset,
  );
}

/** The comparator a freshly-picked field defaults to — the head of its visible set. */
export function defaultComparatorFor(
  definition: FilterDefinition,
): FilterComparator {
  return effectiveComparators(definition)[0];
}

/**
 * Defaults seeded into `useForm` when the user picks a field. Empty
 * `values` array — the refine fails until the user fills something in,
 * which keeps Add filter disabled. The comparator is the head of the
 * field's visible set (`defaultComparatorFor`), so it is always one the
 * OperatorPicker will show.
 */
export function getDefaultsForVariant(definition: FilterDefinition): Filter {
  // `defaultComparatorFor` returns a comparator valid for `definition.type`,
  // so the assembled (type, comparator) pair always matches one Filter
  // variant; the cast just re-narrows the umbrella types back to the union.
  return {
    type: definition.type,
    field: definition.field,
    comparator: defaultComparatorFor(definition),
    values: [],
  } as Filter;
}

/**
 * The values `filter` keeps when its comparator changes to `next`.
 *
 * Values carry over within one field, except where the new comparator takes
 * none, or names a different directory to pick from (an identity's principal
 * type). Leaving `ANY_OF` / `NONE_OF` keeps only the first value: every
 * other comparator edits a single value, so the rest would still apply but
 * could no longer be seen. Entering either keeps the value. Values only: use
 * {@link withComparator} for the whole filter, which also drops the "Not set"
 * member on the way out.
 */
export function valuesForComparator(
  filter: Filter,
  next: FilterComparator,
): Filter["values"] {
  if (isValueless(next) || filter.type === "identity") {
    return [];
  }
  if (isListComparator(filter.comparator) && !isListComparator(next)) {
    return filter.values.slice(0, 1);
  }
  return filter.values;
}

/**
 * `filter` under the comparator `next`, keeping what the new comparator still
 * reads (see {@link valuesForComparator}).
 *
 * Leaving `ANY_OF` / `NONE_OF` also drops `includeNull`: "Not set" is a
 * member only of a list, and the schema rejects it on any other comparator.
 * The key is removed rather than set to `false`, so the result is exactly the
 * filter a fresh pick of `next` would have built. Moving between the two list
 * comparators keeps it.
 */
export function withComparator(filter: Filter, next: FilterComparator): Filter {
  const values = valuesForComparator(filter, next);
  if ("includeNull" in filter && !isListComparator(next)) {
    const { includeNull: _dropped, ...rest } = filter;
    return { ...rest, comparator: next, values } as Filter;
  }
  return { ...filter, comparator: next, values } as Filter;
}
